<app-header></app-header>

<!-- Scan loader (same pattern as face1) -->
<app-image-scan-loader
  [imageSrc]="selectedImageDetails?.thumbnail"
  [percentageComplete]="percentageComplete"
  *ngIf="!allImagesLoaded">
</app-image-scan-loader>

<!-- Main body -->
<div class="main-container d-flex">
  <app-sidebar></app-sidebar>
  <div class="main-section" *ngIf="allImagesLoaded">
  <div class="results-shell">
    <!-- Top bar -->
    <div class="top-bar">
      <div class="left-section">
        <button class="back-btn" (click)="onBackButtonClick()">
          <i class="bi bi-arrow-left"></i>
          <span>Back</span>
        </button>

        <div class="search-box">
          <img class="search-img" [src]="selectedImageDetails?.thumbnail" />
          <span class="search-name" *ngIf="!showSearchNameEditInput" [title]="searchName">
            {{ searchName }}
          </span>
          <input class="search-name-input" type="text" *ngIf="showSearchNameEditInput" [(ngModel)]="searchName"
            (keydown.enter)="saveSearchName()" />
          <button class="search-name-button" type="button" (click)="editSearchName()">
            <i class="bi bi-pencil" *ngIf="!showSearchNameEditInput">
            </i>
            <i class="bi bi-check" *ngIf="showSearchNameEditInput">
            </i>
          </button>
        </div>
      </div>

      <div class="right-section">
        <div class="search-input-wrapper">
          <i class="bi bi-search"></i>
          <input class="search-input" type="text" placeholder="Filter matches" [(ngModel)]="searchText"
            (ngModelChange)="onSearchTextChange()" (keydown.enter)="onSearchTextKeydownEnter()" />
        </div>
        <div class="filter-buttons">
        <button class="filter-pill" [class.active]="selectedFilter === 'all'" (click)="onAllButtonClick()">
          <span>All</span>
          <span class="count-badge">{{ getAllCount() }}</span>
        </button>
        <button class="filter-pill" [class.active]="selectedFilter === 'high'" (click)="onHighButtonClick()">
          <span>High &ge; {{ highProbabilityThreshold * 100 | number : '1.0-0' }}%</span>
          <span class="count-badge">{{ getHighCount() }}</span>
        </button>
        <button class="filter-pill" [class.active]="selectedFilter === 'low'" (click)="onLowButtonClick()">
          <span>Low &lt; {{ highProbabilityThreshold * 100 | number : '1.0-0' }}%</span>
          <span class="count-badge">{{ getLowCount() }}</span>
        </button>
        </div>
      </div>
    </div>

    <!-- Justified gallery (face1): fixed row height, width from image aspect ratio -->
    <div class="results-wrapper">
      <div
        class="results-empty"
        *ngIf="!searchResultsFiltered?.length">
        No matches to display.
      </div>
      <div class="results-grid" #profileCards *ngIf="searchResultsFiltered?.length">
        <div
          class="result-card face-result-card"
          *ngFor="let searchResultFiltered of searchResultsFiltered; let i = index; trackBy: trackBySearchResultIdentity"
          [attr.data-result-id]="resultTrackId(searchResultFiltered)"
          [class.result-card--loading]="!isImageReady(searchResultFiltered)"
          [class.result-card--selected]="
            showSelectedResult &&
            resultTrackId(searchResultFiltered) === resultTrackId(selectedResult)
          "
          (click)="openSelectedResult(searchResultFiltered, i)">
          <img
            class="result-card-image"
            [class.result-card-image--ready]="isImageReady(searchResultFiltered)"
            [src]="getImageSource(searchResultFiltered)"
            alt="Result Image"
            referrerpolicy="no-referrer"
            (load)="onImageLoad($event, searchResultFiltered)"
            (error)="onImageError($event, searchResultFiltered)" />
          <button
            *ngIf="isImageReady(searchResultFiltered) && canStartImageSearch(searchResultFiltered)"
            type="button"
            class="image-search-trigger"
            (click)="startImageSearchFromResult(searchResultFiltered, $event)"
            title="Image Search"
            aria-label="Image Search">
            <i class="fas fa-magnifying-glass" aria-hidden="true"></i>
          </button>
          <div class="result-card-frame" *ngIf="!isImageReady(searchResultFiltered)">
            <div class="skeleton-loader"></div>
          </div>
          <div
            class="result-score"
            *ngIf="isImageReady(searchResultFiltered)"
            [ngClass]="
              searchResultFiltered?.match_probability <
              highProbabilityThreshold
                ? 'match-score-low'
                : 'match-score-high'
            ">
            {{ searchResultFiltered?.match_probability * 100 | number : '1.2-2' }}%
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Result details panel (face1-style) -->
  <div class="result-preview-overlay" *ngIf="showSelectedResult" (click)="closeSelectedResult()"></div>
  <div class="result-preview-modal" *ngIf="showSelectedResult">
    <div class="result-preview">
      <div class="match-bar" [ngClass]="
          selectedResult?.match_probability < highProbabilityThreshold
            ? 'match-bar-low'
            : 'match-bar-high'
        ">
        <div>
          Match Probability :
          {{ selectedResult?.match_probability * 100 | number : '1.2-2' }}%
        </div>
        <div class="image-view-wrapper">
          <button
            class="result-nav-btn"
            type="button"
            title="Previous"
            [disabled]="!canShowPreviousResult"
            (click)="showPreviousResult($event)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <button
            class="result-nav-btn"
            type="button"
            title="Next"
            [disabled]="!canShowNextResult"
            (click)="showNextResult($event)">
            <i class="bi bi-chevron-right"></i>
          </button>
          <a
            class="result-nav-btn"
            [href]="getImageSource(selectedResult)"
            target="_blank"
            rel="noopener noreferrer"
            title="View Image"
            (click)="$event.stopPropagation()">
            <i class="bi bi-binoculars"></i>
          </a>
          <button
            class="result-preview-close"
            type="button"
            title="Close"
            (click)="closeSelectedResult()">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
      <div class="preview-image">
        <img
          [src]="getImageSource(selectedResult)"
          alt="Selected Image"
          referrerpolicy="no-referrer"
          (error)="onImageError($event, selectedResult)" />
        <button
          *ngIf="canStartImageSearch(selectedResult)"
          type="button"
          class="image-search-trigger is-modal"
          (click)="startImageSearchFromResult(selectedResult, $event)"
          title="Image Search"
          aria-label="Image Search">
          <i class="fas fa-magnifying-glass" aria-hidden="true"></i>
          <span>Image Search</span>
        </button>
      </div>
      <div class="preview-content">
        <div *ngIf="contextsMatrix.length === 0">
          <div class="context">
            <div class="context-title">No details found</div>
          </div>
        </div>
        <div *ngIf="contextsMatrix.length > 0">
          <div *ngFor="let contexts of contextsMatrix">
            <div class="context" *ngFor="let context of contexts">
              <div class="context-title">
                {{ context?.key }}
              </div>
              <div class="context-value">
                <ng-container *ngIf="isUrl(context?.value); else textTemplate">
                  <a [href]="context?.value" target="_blank" rel="noopener noreferrer">
                    {{ context?.value }}
                  </a>
                </ng-container>
                <ng-template #textTemplate>
                  {{ context?.value }}
                </ng-template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Draggable query-face thumbnail (face1) -->
  <img
    *ngIf="showSelectedResult && queryFaceThumbnail"
    cdkDrag
    class="query-face-drag"
    [cdkDragFreeDragPosition]="dragPosition"
    (cdkDragEnded)="onDragEnd($event)"
    [src]="queryFaceThumbnail"
    alt="Query face"
    title="Drag to compare" />
  </div>
</div>
